/**
 * PHI / PII redaction — shared between pino's `redact` option and Sentry's
 * `beforeSend` walker. One source of truth: a field that must not appear in
 * pino logs must also not appear in a Sentry payload.
 *
 * Two export shapes, because pino and the walker consume different things:
 *
 *   PHI_PATHS     — dot-path list (`patient.*.dob`, `*.healthCard`, etc.).
 *                   Consumed by pino's `redact` option, which supports a
 *                   single-segment wildcard per path.
 *
 *   PHI_KEY_NAMES — the leaf-key Set derived from PHI_PATHS. Consumed by the
 *                   recursive walker (`containsRedactedPath`) which does a
 *                   key-name match on any depth. The walker is used by Sentry's
 *                   beforeSend (server + browser) — if any key in the event
 *                   tree matches, we drop the entire event. Deliberately
 *                   aggressive: false positives are fine, PHI leaks are not.
 *
 * When adding a new PHI field: add the full path to PHI_PATHS. The leaf name
 * is derived automatically.
 *
 * This module has ZERO external dependencies (no pino, no node built-ins).
 * Safe to import from browser bundles via the `@ruby/observability/redaction`
 * subpath export.
 */
export const PHI_PATHS = [
    // Health card / OHIP
    'healthCard',
    'healthcard',
    'healthCardNumber',
    'hc',
    'hcNumber',
    'versionCode',
    'vc',
    '*.healthCard',
    '*.healthcard',
    '*.hc',
    '*.versionCode',
    // A VELOX note's text in the document card's note writes (ruby-hq
    // `velox-notes-write-api.ts`; plan 2026-11-03-velox-notes-written-from-ruby).
    'noteText',
    'noteBase',
    '*.noteText',
    '*.noteBase',
    // DOB
    'dob',
    'dateOfBirth',
    '*.dob',
    '*.dateOfBirth',
    // Names (first name is lower-risk but still PII — redact by default)
    'lastName',
    'firstName',
    '*.lastName',
    '*.firstName',
    // VELOX WebProxy payload uses PascalCase. The walker matches keys
    // exact-case (`Set.has`), so the camelCase entries above don't cover
    // these. See `services/waiting-room/types.ts:PatientRecord`.
    'FirstName',
    'LastName',
    'MiddleName',
    'PreferredName',
    '*.FirstName',
    '*.LastName',
    '*.MiddleName',
    '*.PreferredName',
    // Pre-composed display name ("First Last") — the VELOX calendar DTO's only
    // patient field. Neither the camelCase nor the PascalCase entries above match
    // it, since it is a single joined string rather than name parts.
    'patientName',
    '*.patientName',
    // Phone / email / address
    'phone',
    'phoneNumber',
    'callerPhone',
    // Operator-stamped voicemail callback number (migration 0058). Set via
    // Dispatch detail-modal pill. PHI risk equivalent to `callerPhone`.
    'callbackPhone',
    '*.callbackPhone',
    // Raw SIP caller-ID. For `ignoreCallerId` clinics this is the clinic's
    // forwarding trunk and not PHI per se, but for all other clinics it IS
    // the patient's actual number — redact uniformly.
    'sipCallerId',
    '*.sipCallerId',
    // Originating caller's phone — written by web-booking onto sessions and
    // interaction metadata when a session arrived via an IVR SMS link. Same
    // PHI risk as `callerPhone`. Plan: docs/plans/2026-05-20-dispatch-journey-linking.md.
    'originCallerPhone',
    'origin_caller_phone',
    '*.phone',
    '*.phoneNumber',
    // A VELOX chart's whole phone list (`{kind, number, ext}` items, plan
    // docs/plans/2026-10-29-velox-card-shows-phones.md): `number` is no PHI name.
    'phones',
    '*.phones',
    '*.callerPhone',
    '*.originCallerPhone',
    '*.origin_caller_phone',
    'email',
    'emailAddress',
    '*.email',
    '*.emailAddress',
    'address',
    'addressLine1',
    '*.address',
    '*.addressLine1',
    // City + postal code (2026-06-13) — the document-manager worker now geocodes
    // patient addresses (Geocodio) for multi-location auto-select and may log
    // geocode failures. `address` was already redacted; `city`/`postal_code` are
    // covered only when nested in a `patient`/`patientJsonb` blob, so redact the
    // bare leaves too. Plan: docs/plans/2026-06-13-document-manager-multi-location.md.
    'city',
    'postal_code',
    'postalCode',
    '*.city',
    '*.postal_code',
    '*.postalCode',
    // Patient prefill / extraction blobs (whole object treated as PHI)
    'prefillJsonb',
    'extraction.patientJsonb',
    'patient',
    'patientJsonb',
    // Request/response bodies — never log raw (can contain anything)
    'req.body',
    'res.body',
    'body',
    // Auth secrets / headers
    'password',
    'token',
    '*.password',
    '*.token',
    'headers.authorization',
    'headers.cookie',
    // Phase 1.5 (2026-05) — operator-typed free-text note attached to workflow
    // transitions. Capped at 1000 chars upstream; redacted in Tier 2 / Tier 4
    // logs because operators may type names, phone numbers, identifiers.
    // Audit JSONL retains the original (compliance Tier 3).
    'freeTextNote',
    '*.freeTextNote',
    // Voicemail transcript captured via IVR press-2 / press-3. Patient may say
    // anything — name, DOB, health card, complaint. Treat the whole string as
    // PHI in pino + Sentry; the value still persists to `interactions.metadata`
    // JSONB which Dispatch reads under existing access control.
    // Plan: docs/plans/2026-05-11-ivr-voicemail-transcription.md.
    'voicemailTranscript',
    '*.voicemailTranscript',
    // Dispatch workflow v1 — operator-typed staff note attached to an
    // interaction. Capped at 1000 chars upstream, but operators commonly
    // include patient names, phone numbers, complaint context. Treat as PHI
    // wherever a payload might carry it (pino + Sentry walker). Note still
    // surfaces in Dispatch under existing client-scope access control.
    // Plan: docs/plans/2026-05-11-dispatch-workflow-v1.md.
    'reviewNote',
    '*.reviewNote',
    // Referring-doctor extraction block. Doctor names + phone + fax +
    // address are PHI-adjacent (a referrer's office number can identify
    // their practice + indirectly a patient cohort). Treat the parent key as
    // the whole-blob redactor — matches the existing `patient` / `patientJsonb`
    // pattern. Adding the per-field leaf names (e.g. `displayName`, `fax`)
    // would over-redact: React component frames carry `displayName`, the
    // wizard's `Trusted patient card` carries `fax`, etc.
    // Plan: docs/plans/2026-05-30-referring-doctors.md (PHI audit).
    'referringDoctor',
    'referring_doctor',
    '*.referringDoctor',
    '*.referring_doctor',
    // Clinical history / indication / reason for referral — unstructured
    // free text on the requisition. Often contains diagnoses, named
    // conditions, rule-outs, family-member references ("post-MVA, R/O
    // fracture, daughter Sarah witnessed"). Both the document-level
    // `clinicalHistory` and the per-procedure `clinicalIndication` cover
    // the same kind of free text and must be treated identically — the
    // per-procedure copy rides on `MatchedService` and can land in
    // procedure-related Sentry payloads.
    // Plan: docs/plans/2026-05-30-clinical-history-field.md (Phase 7).
    'clinicalHistory',
    'clinical_history',
    '*.clinicalHistory',
    '*.clinical_history',
    'clinicalIndication',
    'clinical_indication',
    '*.clinicalIndication',
    '*.clinical_indication',
];
/**
 * Leaf-key Set derived from PHI_PATHS. Stripped of wildcards so the walker
 * can do a simple Set.has() lookup on any node's key name.
 */
export const PHI_KEY_NAMES = new Set(PHI_PATHS.map((p) => p.split('.').pop()).filter((leaf) => leaf && leaf !== '*'));
/**
 * Mask a phone number to its last 4 digits. Use at call sites where an
 * identifying-but-masked form is useful (e.g., "SMS delivery failed for
 * ***-***-1234"). Returns null for null/undefined input.
 */
export function maskPhone(phone) {
    if (!phone)
        return null;
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 4)
        return '***';
    return `***-***-${digits.slice(-4)}`;
}
/**
 * Mask a health card to its last 3 digits. Matches the existing `hcLast3`
 * pattern used elsewhere in the codebase.
 */
export function maskHealthCard(hc) {
    if (!hc)
        return null;
    const digits = hc.replace(/\D/g, '');
    // Require >3 digits before showing a "last 3" suffix. Exactly 3 would
    // show the whole card; pad to all-mask instead.
    if (digits.length <= 3)
        return '***';
    return `*******${digits.slice(-3)}`;
}
/**
 * Recursive walker — returns true if any key in the tree matches a redacted
 * name. O(n·m) on tree size × PHI_KEY_NAMES size; fine for Sentry events
 * (rare, bounded). Don't use on hot paths.
 *
 * Conservative policy: a matching key with ANY non-null/undefined value
 * returns true, even if the nested structure is empty. The `patient` key,
 * for example, is flagged regardless of whether inner fields are populated —
 * assume patient objects carry PHI.
 */
export function containsRedactedPath(obj, keys = PHI_KEY_NAMES, seen = new WeakSet()) {
    if (!obj || typeof obj !== 'object')
        return false;
    // Cycle guard. Sentry events carry Fastify req/reply objects with socket
    // back-references; without this the walker recurses a cycle until the stack
    // blows (RUBY-16). Short-circuiting a re-entered node to `false` is safe: the
    // result is deterministic per node, so a PHI-positive node returns `true` on
    // its FIRST visit and bubbles up before any second reference is reached — only
    // already-evaluated, PHI-negative nodes are ever skipped.
    if (seen.has(obj))
        return false;
    seen.add(obj);
    if (Array.isArray(obj)) {
        return obj.some((item) => containsRedactedPath(item, keys, seen));
    }
    for (const [key, value] of Object.entries(obj)) {
        if (keys.has(key) && value !== null && value !== undefined) {
            return true;
        }
        if (value && typeof value === 'object') {
            if (containsRedactedPath(value, keys, seen))
                return true;
        }
    }
    return false;
}
